// Workflow API types

import { Chatbot, TaskRep } from "../chatbot/types";

export interface MediaPayload {
  type: string;
  url?: string;
  mime_type?: string;
  file_name?: string;
  file_size?: number;
  caption?: string;
}

export interface LocationPayload {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface ChannelInput {
  user_id: string;
  org_id: string;
  command: string;
  text?: string;
  message_type?: string;
  media?: MediaPayload;
  location?: LocationPayload;
  session_id?: string;
}

export interface ChannelResponse {
  channel: string;
  recipient_id: string;
  response_type: string;
  text?: string;
  timestamp: string;
  chatbot?: Chatbot;
  media?: MediaPayload;
  location?: LocationPayload;
  task_rep?: TaskRep;
  last_updated?: TaskRep;
  org_id?: string;
}

export interface ValidationRule {
  required: boolean;
  type?: string;
  accept?: string[];
  min?: number;
  max?: number;
  pattern?: string;
}

export interface WebTask {
  task: TaskRep;
  chatbot: Chatbot;
  ui_component: string;
  validation: ValidationRule;
}

export interface WebTaskItem {
  task: TaskRep;
  ui_component: string;
  validation: ValidationRule;
  is_blocked: boolean;
  display_value: string;
}

export interface WebTaskListResponse {
  tasks: WebTaskItem[];
  chatbot: Chatbot;
  has_makerchecker_pending: boolean;
  makerchecker_task_index: number;
}

export interface WorkflowSummary {
  id: string;
  name: string;
  code: string;
  bot_type: string;
  description?: string;
  total_tasks: number;
  completed_tasks: number;
  pending_tasks: number;
}

/**
 * Active workflow instance - represents a running workflow with pending tasks
 */
export interface WorkflowInstance {
  chatbot_code: string;
  source_key: string;
  chatbot_title: string;
  bot_type: string;
  total_tasks: number;
  completed_tasks: number;
  last_updated: string;
  launched_by?: string; // Mobile number of user who launched the instance (visible to admins)
  due_date?: string | null;
  task_lifecycle?: string; // 'daily' | 'long_running'
}

export interface ProcessCommandRequest {
  user_id: string;
  org_id: string;
  command: string;
  text?: string;
  message_type?: string;
  media?: MediaPayload;
  location?: LocationPayload;
  session_id?: string;
}

/**
 * Task context needed to generate action commands
 */
export interface TaskContext {
  botCode: string;
  taskId: string;
  repId: number;
}

/**
 * Action command patterns for web channel
 * These patterns must match the backend action regex patterns in chatbot/actions/action.go
 */
export const ActionCommands = {
  /**
   * Text input action (text, number, date, address task types)
   * Pattern: input_{botCode}_taskId_{taskId}_repId_{repId}
   */
  text: (ctx: TaskContext): string =>
    `input_${ctx.botCode}_taskId_${ctx.taskId}_repId_${ctx.repId}`,

  /**
   * Media input action (photo, video, document, media task types)
   * Pattern: media_{botCode}_taskId_{taskId}_repId_{repId}
   */
  media: (ctx: TaskContext): string =>
    `media_${ctx.botCode}_taskId_${ctx.taskId}_repId_${ctx.repId}`,

  /**
   * Location input action (location, location_check task types)
   * Pattern: location_{botCode}_taskId_{taskId}_repId_{repId}
   */
  location: (ctx: TaskContext): string =>
    `location_${ctx.botCode}_taskId_${ctx.taskId}_repId_${ctx.repId}`,

  /**
   * Choice selection action
   * Pattern: chatbot_{botCode}_taskId_{taskId}_repId_{repId}_choice_{choiceId}
   */
  choice: (ctx: TaskContext, choiceId: string): string =>
    `chatbot_${ctx.botCode}_taskId_${ctx.taskId}_repId_${ctx.repId}_choice_${choiceId}`,

  /**
   * Skip task action
   * Pattern: chatbot_{botCode}_taskId_{taskId}_skip_{repId}
   */
  skip: (ctx: TaskContext): string =>
    `chatbot_${ctx.botCode}_taskId_${ctx.taskId}_skip_${ctx.repId}`,

  /**
   * Redo task action
   * Pattern: chatbot_{botCode}_redo_{sourceKey}
   */
  redo: (botCode: string, sourceKey: string): string =>
    `chatbot_${botCode}_redo_${sourceKey}`,

  /**
   * WIP confirm action
   * Pattern: chatbot_{botCode}_taskId_{taskId}_wip_{repId}_{yes|no}
   */
  wipConfirm: (ctx: TaskContext, confirm: 'yes' | 'no'): string =>
    `chatbot_${ctx.botCode}_taskId_${ctx.taskId}_wip_${ctx.repId}_${confirm}`,
};
